import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, CheckCircle } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Footer = () => {
  const { language } = useStore();
  const t = translations[language];
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');

  // ============================================================
  //  MODIFICATION : Colle ton URL Cloudinary complète ci-dessous
  // ============================================================
  const MY_CLOUDINARY_LOGO = "https://res.cloudinary.com/darsizywc/image/upload/v1769301672/logos/bzi7ffdtg8oe76hsodn6.png";
  // ============================================================

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language }),
      });

      const data = await response.json();
      
      if (data.already_subscribed) {
        setError(language === 'fr' ? 'Déjà inscrit !' : 'Al ingeschreven!');
      } else {
        setSubscribed(true);
        setEmail('');
      }
    } catch (err) {
      setError(language === 'fr' ? 'Erreur' : 'Fout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-amber-900 text-amber-100" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              {/* --- MODIFICATION : Logo Cloudinary --- */}
              <img 
                src={MY_CLOUDINARY_LOGO} 
                alt="EBB-SHOP Logo"
                style={{
                  width: 'auto',
                  maxHeight: '40px' // La même taille que le cercle original (h-10)
                }}
                className="object-contain"
              />
              {/* -------------------------------------- */}
              <span className="text-xl font-bold text-white">EBB-SHOP</span>
            </div>
            <p className="text-sm text-amber-200">
              {t.footer.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/shop" className="text-sm hover:text-white transition-colors">
                  {t.nav.shop}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm hover:text-white transition-colors">
                  {t.nav.about}
                </Link>
              </li>
              <li>
                <Link to="/size-guide" className="text-sm hover:text-white transition-colors">
                  {t.nav.sizeGuide}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm hover:text-white transition-colors">
                  {t.nav.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.footer.customerService}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/contact" className="text-sm hover:text-white transition-colors">
                  {t.nav.contact}
                </Link>
              </li>
              <li>
                <Link to="/legal/returns" className="text-sm hover:text-white transition-colors">
                  {t.footer.returns}
                </Link>
              </li>
              <li>
                <Link to="/legal/privacy" className="text-sm hover:text-white transition-colors">
                  {t.footer.privacy}
                </Link>
              </li>
              <li>
                <Link to="/legal/terms" className="text-sm hover:text-white transition-colors">
                  {t.footer.terms}
                </Link>
              </li>
              <li>
                <Link to="/downloads" className="text-sm hover:text-white transition-colors">
                  {language === 'fr' ? 'Téléchargements' : language === 'nl' ? 'Downloads' : 'Downloads'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.footer.newsletter}</h3>
            {subscribed ? (
              <div className="flex items-center gap-2 text-green-300">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm">
                  {language === 'fr' ? 'Merci pour votre inscription !' : 'Bedankt voor uw inschrijving!'}
                </span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit}>
                <div className="flex">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.footer.newsletterPlaceholder}
                    className="flex-1 px-3 py-2 rounded-l-md text-gray-900 text-sm focus:outline-none"
                    required
                    data-testid="newsletter-email-input"
                  />
                  <button 
                    type="submit"
                    disabled={loading}
                    className="bg-amber-700 hover:bg-amber-600 disabled:bg-amber-800 px-4 py-2 rounded-r-md transition-colors"
                    data-testid="newsletter-submit-btn"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                </div>
                {error && (
                  <p className="text-xs text-red-300 mt-1">{error}</p>
                )}
              </form>
            )}
            <div className="flex space-x-4 mt-6">
              <a href="#" className="hover:text-white transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="hover:text-white transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-amber-800 mt-8 pt-8 text-center text-sm text-amber-300">
          <p>© {new Date().getFullYear()} EBB-SHOP. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;